import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { CXQuestionBlock } from '~/components/ClientExperience/OneFlow/models/cx-question-block.model';
import { IDateQuestion, QuestionType } from '~/types/builder.types';
import { timeOptions } from '~/utils/date.utils';
import { IDateQuestionValue } from '~/components/ClientExperience/OneFlow/FlowContent/QuestionBlock/MultiInputQuestions/DateQuestion/CXDateQuestion.types';
import { dateOrderError } from '~/components/ClientExperience/OneFlow/components/FlowView/error-messages';
import { CXSignatureBlock } from '~/components/ClientExperience/OneFlow/models/cx-signature-block.model';
import { CXContractHTMLBlock } from '~/components/ClientExperience/OneFlow/models/cx-contract-html-block.model';
import {
    ISmartField,
    ServiceSelectionType,
    TInputTypeValidation
} from '~/types/model.types';
import { CXChargeBlock } from '~/components/ClientExperience/OneFlow/models/cx-charge-block.model';
import { CXFlow } from '~/components/ClientExperience/OneFlow/models/cx-flow.model';
import { CXServiceSelectionBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-selection-block.model';
import { getValueFromStorage } from '~/common/hooks/useStorage';
import { CXSignaturesBlock } from '~/components/ClientExperience/OneFlow/models/cx-signatures-block.model';
import { joinListWithCommasAndFinalConnector } from '~/utils/text.utils';
import {
    IGetFormikPropsParams,
    IValidation,
    IValidator,
    ValidationErrorTypes
} from '~/components/ClientExperience/OneFlow/components/FlowView/FlowForm.types';
import { CXInvoiceBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-block.model';
import type { Variable } from '~/models/variable.model';
import type { CXScheduleContainerBlock } from '~/components/ClientExperience/OneFlow/models/cx-schedule-container-block.model';
import { URL_REGEX } from '~/utils/url.utils';

export const SIGNATURES_BLOCK_FIELD_NAME = 'signaturesBlock';
export const PENDING_SELECTION_FIELD_NAME = 'pending_selectionBlock_id';
export const PENDING_INVOICE_FIELD_NAME = 'is_pending_invoice';

export const FORM_FIELDS_TO_SKIP_VALIDATION: string[] = [
    'transaction_id',
    SIGNATURES_BLOCK_FIELD_NAME,
    PENDING_SELECTION_FIELD_NAME,
    PENDING_INVOICE_FIELD_NAME
];

function getValidationForDateVariable(question: IDateQuestion): IValidation {
    const innerSchema: any = {};
    const validator = yup
        .string()
        .required({ type: ValidationErrorTypes.RequiredQuestion });
    if (question.is_required) {
        innerSchema.start_date = validator;

        if (question.has_time) {
            innerSchema.start_time = validator;
        }

        if (question.has_end_date) {
            innerSchema.end_date = validator;

            if (question.has_time) {
                innerSchema.end_time = validator;
            }
        }
    }

    return yup
        .object(innerSchema)
        .test(
            'is-date-bigger',
            dateOrderError(question),
            ({
                start_date,
                end_date,
                start_time,
                end_time
            }: Partial<IDateQuestionValue> = {}) => {
                if (
                    start_date &&
                    end_date &&
                    new Date(end_date) < new Date(start_date)
                ) {
                    return false;
                }

                if (
                    !(
                        start_date &&
                        end_date &&
                        new Date(start_date) < new Date(end_date)
                    ) &&
                    start_time &&
                    end_time &&
                    timeOptions.findIndex(x => x.value === end_time) <=
                        timeOptions.findIndex(x => x.value === start_time)
                ) {
                    return false;
                }

                return true;
            }
        );
}

function getValidationForQuestionVariable({
    question
}: CXQuestionBlock): IValidation {
    if (question.question_type === QuestionType.Date) {
        return getValidationForDateVariable(question);
    }

    const questionTypesToValidate = [
        QuestionType.ShortText,
        QuestionType.Number,
        QuestionType.URL
    ];

    if (
        questionTypesToValidate.includes(question.question_type) &&
        question.validation_type
    ) {
        const requiredValidation: Record<TInputTypeValidation, IValidation> = {
            email: yup
                .string()
                .email({ type: ValidationErrorTypes.EmailInput })
                .required({ type: ValidationErrorTypes.RequiredQuestion }),
            number: yup
                .number({ type: ValidationErrorTypes.NumberInput })
                .required({ type: ValidationErrorTypes.RequiredQuestion }),
            url: yup
                .string()
                .matches(URL_REGEX, {
                    message: {
                        type: ValidationErrorTypes.URLInput
                    }
                })
                .required({ type: ValidationErrorTypes.RequiredQuestion })
        };

        const validation: Record<TInputTypeValidation, IValidation> = {
            email: yup
                .string()
                .email({ type: ValidationErrorTypes.EmailInput }),
            number: yup.number({
                type: ValidationErrorTypes.NumberInput
            }),
            url: yup.string().matches(URL_REGEX, {
                message: {
                    type: ValidationErrorTypes.URLInput
                }
            })
        };

        return question.is_required
            ? requiredValidation[question.validation_type]
            : validation[question.validation_type];
    }

    if (question.is_required) {
        return yup
            .string()
            .required({ type: ValidationErrorTypes.RequiredQuestion });
    }
}

function getValidationForSignatureVariable(
    { signature }: CXSignatureBlock,
    currUserId: string
): IValidation {
    if (signature.required && signature.signee_id === currUserId) {
        return yup.object({
            [ValidationErrorTypes.Signature]: yup
                .string()
                .required({ type: ValidationErrorTypes.Signature })
                .nullable(true)
        });
    }
}

export const generateContractProgressError = (
    { unsignedRequiredClientSignatures }: CXSignaturesBlock,
    currentUserId
): string => {
    const currentUserPendingSignature = unsignedRequiredClientSignatures.find(
        signature => signature.signeeId === currentUserId
    );
    const isCurrentUserPendingInvalidatedSignature =
        !!currentUserPendingSignature?.connectedVariable?.invalidated_on;

    const unsignedRequiredOtherClientsSignatures =
        unsignedRequiredClientSignatures.filter(
            signatureBlock =>
                signatureBlock.signature.signee_id !== currentUserId
        );

    if (isCurrentUserPendingInvalidatedSignature) {
        return `To continue, approve changes on this page and re-sign the contract.`;
    } else if (currentUserPendingSignature) {
        return `To continue, first you need to sign the contract.`;
    } else if (unsignedRequiredOtherClientsSignatures?.length) {
        const signeeNames = unsignedRequiredOtherClientsSignatures.map(
            signature => signature.signature.signee_name
        );
        const missingSignatureNames = joinListWithCommasAndFinalConnector(
            currentUserPendingSignature ? ['you', ...signeeNames] : signeeNames,
            'and'
        );

        return `To continue, the contract must be signed by: ${missingSignatureNames}.`;
    }
};

function getValidationForSignaturesBlock(currUserId: string): IValidation {
    return yup
        .object()
        .shape({
            unsignedRequiredClientSignatures: yup.array().of(yup.object())
        })
        .test({
            name: ValidationErrorTypes.PendingSignature,
            message: signaturesField => ({
                [ValidationErrorTypes.PendingSignature]: {
                    type: ValidationErrorTypes.PendingSignature,
                    message: generateContractProgressError(
                        signaturesField.originalValue,
                        currUserId
                    )
                }
            }),
            test: ({ unsignedRequiredClientSignatures }) =>
                !unsignedRequiredClientSignatures?.length
        });
}

function getValidationForPendingInvoiceBlock(): IValidation {
    return yup.boolean().test({
        name: ValidationErrorTypes.PendingInvoice,
        message: {
            [ValidationErrorTypes.PendingInvoice]: {
                type: ValidationErrorTypes.PendingInvoice
            }
        },
        test: (isPendingInvoice: boolean) => {
            return isPendingInvoice ? false : true;
        }
    });
}

function getValidationForContractSmartFieldVariable(
    smartField: ISmartField
): IValidation {
    if (smartField.fill_type === 'client_required') {
        return yup.string().required({ type: ValidationErrorTypes.SmartField });
    }
}

function getValidationForServiceSelectionVariable({
    selection_required,
    selection_type
}: CXServiceSelectionBlock): IValidation {
    if (
        selection_required &&
        selection_type !== ServiceSelectionType.NoSelection
    ) {
        return yup.object({
            [ValidationErrorTypes.Selection]: yup
                .array()
                .required({ type: ValidationErrorTypes.Selection })
                .nullable(true)
        });
    }
}

function getValidationForPendingServiceSelection() {
    return yup.string().test({
        name: ValidationErrorTypes.PendingSelection,
        message: {
            [ValidationErrorTypes.PendingSelection]: {
                type: ValidationErrorTypes.PendingSelection
            }
        },
        // if no service selection block pending approval ,test passes
        test: pendingServiceSelectionId => {
            return pendingServiceSelectionId === undefined ? true : false;
        }
    });
}

function getValidationForChargeBlock(): IValidation {
    return yup.string().required({ type: ValidationErrorTypes.ChargeBlock });
}

function getValidationForScheduleBlock(
    block: CXScheduleContainerBlock
): IValidation {
    return yup.boolean().test({
        message: { type: ValidationErrorTypes.ScheduleSession },
        test: () => block?.isValidForSubmission
    });
}

function createValidationConfigs(
    flow: CXFlow,
    questionBlocks: CXQuestionBlock[],
    signatureBlocks: CXSignatureBlock[],
    signaturesBlock: CXSignaturesBlock,
    contractHtmlBlocks: CXContractHTMLBlock[],
    serviceSelectionBlocks: CXServiceSelectionBlock[],
    chargeBlock: CXChargeBlock,
    invoiceBlock: CXInvoiceBlock,
    scheduleContainerBlock: CXScheduleContainerBlock,
    currUserId: string
): Array<IValidator> {
    const allBlocks: Array<IValidator> = [];
    allBlocks.push(
        ...questionBlocks.map(
            (block: CXQuestionBlock): IValidator => ({
                id: block.connected_variable_id,
                validator: getValidationForQuestionVariable(block)
            })
        )
    );

    allBlocks.push(
        ...signatureBlocks.map(
            (block: CXSignatureBlock): IValidator => ({
                id: block.connected_variable_id,
                validator: getValidationForSignatureVariable(block, currUserId)
            })
        )
    );

    if (signaturesBlock) {
        allBlocks.push({
            id: SIGNATURES_BLOCK_FIELD_NAME,
            validator: getValidationForSignaturesBlock(currUserId)
        });
    }

    contractHtmlBlocks.forEach((block: CXContractHTMLBlock) => {
        allBlocks.push(
            ...block.contract.fields_metadata.map(
                (smartField: ISmartField): IValidator => ({
                    id: smartField.connected_variable_id,
                    validator:
                        getValidationForContractSmartFieldVariable(smartField)
                })
            )
        );
    });

    allBlocks.push(
        ...serviceSelectionBlocks.map(
            (block: CXServiceSelectionBlock): IValidator => ({
                id: block.connected_variable_id,
                validator: getValidationForServiceSelectionVariable(block)
            })
        )
    );

    if (flow.pendingApprovalBlocks && flow.hasServiceSelectionBlocks) {
        allBlocks.push({
            id: PENDING_SELECTION_FIELD_NAME,
            validator: getValidationForPendingServiceSelection()
        });
    }

    // if there is an option to pay cant submit
    if (chargeBlock && flow.isClientMustPay) {
        allBlocks.push({
            id: 'transaction_id',
            validator: getValidationForChargeBlock()
        });
    }

    if (flow.pendingApprovalBlocks && invoiceBlock) {
        allBlocks.push({
            id: PENDING_INVOICE_FIELD_NAME,
            validator: getValidationForPendingInvoiceBlock()
        });
    }

    if (scheduleContainerBlock) {
        allBlocks.push({
            id: ValidationErrorTypes.ScheduleSession,
            validator: getValidationForScheduleBlock(scheduleContainerBlock)
        });
    }

    return allBlocks;
}

function createDynamicSchema(validationConfigs: Array<IValidator>) {
    return validationConfigs.reduce((schema, config) => {
        const { id, validator } = config;
        if (validator && Object.keys(validator).length) {
            schema[id] = validator;
        }

        return schema;
    }, {} as ObjectShape);
}

function createValidationSchema(
    flow: CXFlow,
    questionBlocks: CXQuestionBlock[],
    signatureBlocks: CXSignatureBlock[],
    signaturesBlock: CXSignaturesBlock,
    contractHtmlBlocks: CXContractHTMLBlock[],
    serviceSelectionBlocks: CXServiceSelectionBlock[],
    chargeBlock: CXChargeBlock,
    invoiceBlock: CXInvoiceBlock,
    scheduleContainerBlock: CXScheduleContainerBlock,
    currUserId: string
) {
    const validationConfigs = createValidationConfigs(
        flow,
        questionBlocks,
        signatureBlocks,
        signaturesBlock,
        contractHtmlBlocks,
        serviceSelectionBlocks,
        chargeBlock,
        invoiceBlock,
        scheduleContainerBlock,
        currUserId
    );

    return createDynamicSchema(validationConfigs);
}

export function getFormikProps({
    selectedFlow,
    selectedPage,
    flowViewUiStore,
    flowStore,
    currUserId = '',
    skipValidation = false,
    isPreview = false
}: IGetFormikPropsParams) {
    const questionBlocks = selectedPage.blocks.filter(
        block => block.type === 'question_block'
    ) as CXQuestionBlock[];
    const signatureBlocks = selectedPage.blocks.filter(
        block => block.type === 'signature_block'
    ) as CXSignatureBlock[];
    const contractHtmlBlocks = selectedPage.blocks.filter(
        block => block.type === 'contract_html_block'
    ) as CXContractHTMLBlock[];
    const serviceSelectionBlocks = selectedPage.blocks.filter(
        block => block.type === 'service_selection_block'
    ) as CXServiceSelectionBlock[];
    const signaturesBlock = selectedPage.blocks.find(
        block => block.type === 'signatures_block'
    ) as CXSignaturesBlock;
    const invoiceBlock = selectedPage.blocks.find(
        block => block.type === 'invoice_container_block'
    ) as CXInvoiceBlock;
    const { scheduleContainerBlock } = selectedPage;

    const initialGeneralValues = [
        ...questionBlocks,
        ...signatureBlocks,
        ...serviceSelectionBlocks
    ].reduce(
        (_initialValues, block) => {
            _initialValues[block.connected_variable_id] =
                block.variableRawValue ?? undefined;

            return _initialValues;
        },
        { page_id: selectedPage.page_id }
    );

    if (
        selectedPage.page_id &&
        flowViewUiStore.pageSavedData &&
        selectedPage.page_id === flowViewUiStore.pageSavedData.page_id
    ) {
        const pageRestoredValues = Object.keys(initialGeneralValues).reduce(
            (savedGeneralValues, variableId) => {
                const savedValue = flowViewUiStore.pageSavedData[variableId];
                if (!savedValue) {
                    return savedGeneralValues;
                }

                savedGeneralValues[variableId] = savedValue;

                return savedGeneralValues;
            },
            {}
        );

        Object.assign(initialGeneralValues, pageRestoredValues);
        flowViewUiStore.setPageSavedData(null);
    }

    // Charge block transactionId validation
    const chargeBlock = selectedPage.blocks.find(
        blk => blk.type === 'charge_block'
    ) as CXChargeBlock;
    // const initialChargeBlockValues =
    //     chargeBlock && selectedFlow.currentPayment
    //         ? { transaction_id: selectedFlow.currentPayment.charge_id || '' }
    //         : {};

    // signatures block validations
    const { unsignedRequiredClientSignatures } = signaturesBlock || {};
    const initialSignaturesBlockValidations = signaturesBlock
        ? {
              [SIGNATURES_BLOCK_FIELD_NAME]: {
                  unsignedRequiredClientSignatures
              }
          }
        : {};

    // Get Local storage variables and values for contract section
    const _cachedValuesContract = !selectedPage.completed
        ? getValueFromStorage({
              key: `${selectedFlow._id}_cxStorage`,
              path: `${selectedPage.page_id}.contract`
          })
        : {};

    // Local storage variables and values
    const _cachedValues = !selectedPage.completed
        ? getPageValuesFromLocalStorage({
              flowId: selectedFlow._id,
              pageId: selectedPage.page_id
          })
        : {};

    // Variables might not exist anymore on the flow but still be in the clients local storage
    const existingCachedValuesContract = filterOnlyExistingVariables(
        selectedFlow.activeRevision.variablesMap,
        _cachedValuesContract
    );
    const existingCachedValues = filterOnlyExistingVariables(
        selectedFlow.activeRevision.variablesMap,
        _cachedValues
    );

    // Updating values on flowStore
    Object.keys(existingCachedValuesContract || {}).forEach(variableId => {
        flowStore.setVariable(
            variableId,
            existingCachedValuesContract[variableId],
            isPreview
        );
    });

    // Merge values for Smart Field with localStorage values
    const initialSmartFieldsValues = {
        ...contractHtmlBlocks[0]?.variablesValues,
        ...existingCachedValuesContract
    };

    const initialValues = {
        ...initialGeneralValues,
        ...initialSmartFieldsValues,
        // ...initialChargeBlockValues,
        ...initialSignaturesBlockValidations,
        ...existingCachedValues
    };

    // Don't validate anything on preview. In the future we could select specific validations to
    // run on preview. We actually want to run validations in previw, but at the moment it's problematic. For example:
    // In preview you can't pay, or sign the contract as a client so you won't be able to proceed
    const validationSchema = skipValidation
        ? {}
        : createValidationSchema(
              selectedFlow,
              questionBlocks,
              signatureBlocks,
              signaturesBlock,
              contractHtmlBlocks,
              serviceSelectionBlocks,
              chargeBlock,
              invoiceBlock,
              scheduleContainerBlock,
              currUserId
          );

    return {
        initialValues,
        validationSchema: yup.object().shape(validationSchema)
    };
}

export const updateFormDataInLocalStorage = ({
    flowId,
    pageId,
    questionVariablesIds = [],
    questionVariables
}) => {
    const { flowCachedData, flowCachedKey } = getFlowCachedDataFromLocalStorage(
        { flowId }
    );
    const pageCachedData = flowCachedData[pageId] || {};

    let changed = false;
    questionVariablesIds.forEach(variableId => {
        if (pageCachedData[variableId] !== questionVariables[variableId]) {
            pageCachedData[variableId] = questionVariables[variableId];
            changed = true;
        }
    });
    if (changed) {
        flowCachedData[pageId] = pageCachedData;
        localStorage.setItem(flowCachedKey, JSON.stringify(flowCachedData));
    }
};

// Get the flow entry on local storage element
const getFlowCachedDataFromLocalStorage = ({ flowId }) => {
    const flowCachedKey = `${flowId}_questions`;
    const flowCachedDataStr = localStorage.getItem(flowCachedKey) || '{}';
    const flowCachedData = JSON.parse(flowCachedDataStr);
    return { flowCachedData, flowCachedKey };
};

// Get all values of the given page out of local storage
const getPageValuesFromLocalStorage = ({ flowId, pageId }) => {
    const { flowCachedData } = getFlowCachedDataFromLocalStorage({ flowId });
    return flowCachedData[pageId] || {};
};

// Filter out variables that do not exist in the variablesMap
const filterOnlyExistingVariables = (
    variablesMap: Record<string, Variable<any>> = {},
    variables: Record<string, any> = {}
) => {
    return Object.keys(variables).reduce((filteredVariables, variableId) => {
        if (variablesMap[variableId]) {
            filteredVariables[variableId] = variables[variableId];
        }
        return filteredVariables;
    }, {});
};
